import { useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, MenuItem, Typography } from "@mui/material"
import { FormikDropdown, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { FormikAllergensField } from "../../../../admin/products/components/FormikAllergensField"
import { FormikCurrencyField } from "../../../../admin/products/components/FormikCurrencyField"
import { FormikImageUpload } from "../../../../../../shared/components/Formik/FormikImageUpload"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CreateUpdateDayMenuFormValues } from "../../menuPlanningTypes"
import { DayMenuAddButton } from "./FormElements"
import { AVAILABLE_DISH } from "./CreateUpdateDayMenuFormContent"

const useStyles = makeStyles()((theme) => ({
    button: {
        fontSize: "0.8rem",
        textTransform: "none",
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    accordion: {
        boxShadow: "none",
        border: "1px solid rgba(0, 0, 0, 0.12)",
        "&:before": {
            display: "none",
        },
    },
}))

export const defaultDish = {
    name: "",
    description: "",
    productDescription: { allergens: [] },
}

export function DishList() {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateUpdateDayMenuFormValues>()
    const defaultPriceBox = { price: undefined, currency: "DKK" }
    const dishesLength = values.dishes.length

    const [expanded, setExpanded] = useState<number | false>(0)

    const handleAddAccordion = () => {
        setFieldValue("dishes", [...values.dishes, defaultDish])
        setExpanded(dishesLength)
    }
    const handleAddMarketingDetails = (index: number) => {
        const dishes = [...values.dishes]
        dishes[index] = {
            ...dishes[index],
            ...defaultPriceBox,
            productPhoto: { url: "" },
        }
        setFieldValue("dishes", dishes)
    }

    const handleAccordionChange = (panelIndex: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panelIndex : false)
    }
    const lastDishesIndex = dishesLength - 1
    const disabledAddButton = dishesLength > 0 && !values.dishes[lastDishesIndex].name

    return (
        <>
            {!!dishesLength && (
                <Grid container flexDirection="column" gap={3}>
                    {values.dishes.map((item, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === index}
                            onChange={handleAccordionChange(index)}
                            className={classes.accordion}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}a-content`}
                                id={`panel${index}a-header`}
                            >
                                {item.name ? (
                                    <Typography variant="body1" fontWeight={700}>
                                        {item.name}
                                    </Typography>
                                ) : (
                                    <LocalizedStrict
                                        id="merchant-menu-planning-dialog-dish"
                                        vars={{ countOfDish: index + 1 }}
                                    >
                                        <Typography variant="body1">{index + 1}</Typography>
                                    </LocalizedStrict>
                                )}
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container gap={2.5} flexDirection="column">
                                    <LocalizedStrict
                                        id="merchant-menu-planning-dialog-name-of-dish-field"
                                        attrs={{ label: true }}
                                    >
                                        <FormikTextField
                                            name={`dishes[${index}].name`}
                                            label="Name of dish *"
                                            fullWidth
                                        />
                                    </LocalizedStrict>
                                    <LocalizedStrict
                                        id="merchant-menu-planning-dialog-brief-field"
                                        attrs={{ label: true, placeholder: true }}
                                    >
                                        <FormikTextField
                                            name={`dishes[${index}].description`}
                                            label="Brief"
                                            placeholder="Short description, max. 80 characters including space."
                                            fullWidth
                                            inputProps={{ maxLength: 80 }}
                                        />
                                    </LocalizedStrict>
                                    <FormikAllergensField name={`dishes[${index}].productDescription.allergens`} />

                                    {!(item.productDescription?.marketingTag || item.currency || item.productPhoto) ? (
                                        <LocalizedStrict
                                            id="merchant-menu-planning-dialog-add-marketing-details-button"
                                            attrs={{ title: true }}
                                        >
                                            <DayMenuAddButton
                                                title="+ Add marketing details"
                                                handleClick={() => handleAddMarketingDetails(index)}
                                            />
                                        </LocalizedStrict>
                                    ) : (
                                        <>
                                            <LocalizedStrict
                                                id="merchant-menu-planning-dialog-marketing-tag-field"
                                                attrs={{ label: true, helperText: true }}
                                            >
                                                <FormikTextField
                                                    name={`dishes[${index}].productDescription.marketingTag`}
                                                    label="Marketing tag"
                                                    helperText="Examples: Offer of the day, Discount"
                                                    fullWidth
                                                />
                                            </LocalizedStrict>

                                            <Grid container item spacing={1} direction="row">
                                                <Grid item sm={2.5}>
                                                    <LocalizedStrict
                                                        id="merchant-menu-planning-dialog-currency-field"
                                                        attrs={{ label: true }}
                                                    >
                                                        <FormikDropdown
                                                            name={`dishes[${index}].currency`}
                                                            label="Currency"
                                                            fullWidth
                                                        >
                                                            <MenuItem value="DKK">DKK</MenuItem>
                                                        </FormikDropdown>
                                                    </LocalizedStrict>
                                                </Grid>
                                                <Grid item sm={3}>
                                                    <LocalizedStrict
                                                        id="merchant-menu-planning-dialog-price-field"
                                                        attrs={{ label: true }}
                                                    >
                                                        <FormikCurrencyField
                                                            name={`dishes[${index}].price`}
                                                            label="Price"
                                                        />
                                                    </LocalizedStrict>
                                                </Grid>
                                            </Grid>

                                            <LocalizedStrict
                                                id="merchant-menu-planning-dialog-upload-image"
                                                attrs={{ label: true }}
                                            >
                                                <FormikImageUpload
                                                    label="Upload image"
                                                    name={`dishes[${index}].productPhoto`}
                                                    typeOfEntity="dayMenu"
                                                />
                                            </LocalizedStrict>
                                        </>
                                    )}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Grid>
            )}

            <Grid container justifyContent="space-between" alignItems="center">
                <LocalizedStrict id="merchant-menu-planning-dialog-add-dish-button">
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={disabledAddButton}
                        // FIXME: Temporarily disabled for conference; reenable after
                        // disabled={disabledAddButton || dishesLength === AVAILABLE_DISH}
                        onClick={handleAddAccordion}
                        className={classes.button}
                    >
                        + Add dish
                    </Button>
                </LocalizedStrict>
                {/* FIXME: Temporarily disabled for conference; reenable after */}
                {/* {dishesLength === AVAILABLE_DISH && (
                    <LocalizedStrict
                        id="merchant-menu-planning-dialog-max-dishes"
                        vars={{ countOfDishes: AVAILABLE_DISH }}
                    >
                        <Typography variant="body2">Available dish signs: {AVAILABLE_DISH}</Typography>
                    </LocalizedStrict>
                )} */}
            </Grid>
        </>
    )
}
